import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useChartColors } from '../../hooks';
import './BurnupArea.css';

export interface BurnupPoint {
  label: string;
  total: number;
  completed: number;
}

interface BurnupAreaProps {
  points: BurnupPoint[];
}

interface TooltipPayload { value: number; dataKey: string; }

const BurnTooltip: React.FC<{ active?: boolean; payload?: TooltipPayload[]; label?: string; scopeColor: string; doneColor: string }> = ({ active, payload, label, scopeColor, doneColor }) => {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.find(p => p.dataKey === 'total')?.value ?? 0;
  const completed = payload.find(p => p.dataKey === 'completed')?.value ?? 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="burnup-tooltip">
      <span className="burnup-tt-date">{label}</span>
      <span className="burnup-tt-row"><i style={{ background: scopeColor }} />Scope<strong>{total}</strong></span>
      <span className="burnup-tt-row"><i style={{ background: doneColor }} />Done<strong>{completed}</strong></span>
      <span className="burnup-tt-pct">{pct}% complete</span>
    </div>
  );
};

export const BurnupArea: React.FC<BurnupAreaProps> = ({ points }) => {
  const colors = useChartColors();
  const scopeColor = colors.muted;
  const doneColor = colors.green;

  if (points.length === 0) {
    return <div className="burnup-empty">No task data to chart yet.</div>;
  }

  return (
    <div className="burnup-wrap">
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={points} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <defs>
            <linearGradient id="burnupDone" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={doneColor} stopOpacity={0.28} />
              <stop offset="100%" stopColor={doneColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: colors.axis, fontSize: 11 }} axisLine={{ stroke: colors.grid }} tickLine={false} minTickGap={12} />
          <YAxis tick={{ fill: colors.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
          <Tooltip content={<BurnTooltip scopeColor={scopeColor} doneColor={doneColor} />} />
          <Area
            type="monotone" dataKey="total" name="Total Scope"
            stroke={scopeColor} strokeWidth={2} strokeDasharray="4 3"
            fill="transparent" dot={false} activeDot={{ r: 4, fill: scopeColor }}
            isAnimationActive
          />
          <Area
            type="monotone" dataKey="completed" name="Completed"
            stroke={doneColor} strokeWidth={2.5}
            fill="url(#burnupDone)" dot={false} activeDot={{ r: 4, fill: doneColor }}
            isAnimationActive
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
